import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import './QrisPage.css';
import { WA_CS_LINK } from './config/whatsapp';
import { useLanguage } from './i18n/LanguageContext';

// Countdown sentinels, rendered through the dictionary at display time.
const COUNTDOWN_EXPIRED = 'EXPIRED';
const COUNTDOWN_UNKNOWN = 'UNKNOWN';

function parseUTCDatetime(datetimeStr) {
    return new Date(datetimeStr.replace(' ', 'T') + 'Z');
}

const QrisPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { qrString, expiryTime, orderId, amount } = location.state || {};

    const [countdown, setCountdown] = useState('');
    const [checkingStatus, setCheckingStatus] = useState(false);
    // { key, vars } rather than a string, so it follows a language switch.
    const [statusMessage, setStatusMessage] = useState(null);
    const qrRef = useRef(null);
    const [supportsShare, setSupportsShare] = useState(false);

    useEffect(() => {
        const hasShare = typeof navigator !== 'undefined' && 'canShare' in navigator && 'share' in navigator;
        setSupportsShare(hasShare);
    }, []);

    useEffect(() => {
        if (!orderId) return;
        const intervalId = setInterval(() => {
            fetch(`https://api.artamobile.id/v1/order/${orderId}/status`)
                .then(res => res.json())
                .then(data => {
                    const s = data.status;
                    if (s === 'PAID' || s === 'ESIM_PUBLISHED') {
                        clearInterval(intervalId);
                        navigate(`/payment-status?orderId=${orderId}`);
                    }
                })
                .catch(err => console.error('Error fetching payment status:', err));
        }, 5000);
        return () => clearInterval(intervalId);
    }, [orderId, navigate]);

    useEffect(() => {
        if (!expiryTime) return;
        const expiryDate = parseUTCDatetime(expiryTime);
        if (isNaN(expiryDate.getTime())) { setCountdown(COUNTDOWN_UNKNOWN); return; }
        const intervalId = setInterval(() => {
            const diffMs = expiryDate - new Date();
            if (diffMs <= 0) { setCountdown(COUNTDOWN_EXPIRED); clearInterval(intervalId); }
            else {
                const m = Math.floor(diffMs / 60000);
                const s = Math.floor((diffMs % 60000) / 1000);
                setCountdown(`${m}:${s < 10 ? '0' + s : s}`);
            }
        }, 1000);
        return () => clearInterval(intervalId);
    }, [expiryTime]);

    const isExpired = countdown === COUNTDOWN_EXPIRED;
    const timerLabel = isExpired
        ? t('qris.expired')
        : countdown === COUNTDOWN_UNKNOWN
            ? t('qris.unknownExpiry')
            : t('qris.expiresIn', { time: countdown });

    const createQRBlob = async () => {
        const svg = qrRef.current?.querySelector('svg');
        if (!svg) return null;
        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(svg);
        const canvas = document.createElement('canvas');
        const size = 200, padding = 24, scale = 3;
        canvas.width = (size + padding * 2) * scale;
        canvas.height = (size + padding * 2) * scale;
        return await new Promise(resolve => {
            const ctx = canvas.getContext('2d');
            const img = new Image();
            img.onload = () => {
                ctx.fillStyle = '#fff';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.setTransform(scale, 0, 0, scale, 0, 0);
                ctx.drawImage(img, padding, padding);
                canvas.toBlob(blob => resolve(blob), 'image/png', 1.0);
            };
            img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);
        });
    };

    const handleDownloadQR = async () => {
        const blob = await createQRBlob();
        if (!blob) return;
        const link = document.createElement('a');
        link.download = `qris-${orderId || 'code'}.png`;
        link.href = URL.createObjectURL(blob);
        link.click();
        URL.revokeObjectURL(link.href);
    };

    const handleSaveOrDownload = async () => {
        const blob = await createQRBlob();
        if (!blob) return;
        const file = new File([blob], `qris-${orderId || 'code'}.png`, { type: 'image/png' });
        try {
            if (supportsShare && navigator.canShare?.({ files: [file] })) {
                await navigator.share({ files: [file], title: t('qris.shareTitle'), text: t('qris.shareText') });
            } else {
                await handleDownloadQR();
            }
        } catch {
            await handleDownloadQR();
        }
    };

    const handleCheckStatus = async () => {
        if (!orderId) return;
        setCheckingStatus(true);
        setStatusMessage(null);
        try {
            const res = await fetch(`https://api.artamobile.id/v1/order/${orderId}/status`);
            const data = await res.json();
            const s = data.status;
            if (s === 'PAID' || s === 'ESIM_PUBLISHED') {
                navigate(`/payment-status?orderId=${orderId}`);
                return;
            }
            if (s === 'CREATED') setStatusMessage({ key: 'qris.statusCreated' });
            else if (s === 'EXPIRED') setStatusMessage({ key: 'qris.statusExpired' });
            else setStatusMessage({ key: 'qris.statusOther', vars: { status: s } });
        } catch {
            setStatusMessage({ key: 'qris.statusError' });
        } finally {
            setCheckingStatus(false);
        }
    };

    const fmt = (v) => `Rp ${Number(v || 0).toLocaleString('id-ID')}`;

    return (
        <div className="qp-page">

            {/* ── App Bar ── */}
            <div className="qp-appbar">
                <button className="qp-back-btn" onClick={() => navigate(-1)} aria-label={t('common.back')}>
                    <img src={`${process.env.PUBLIC_URL}/icons/arrow-left.svg`} width={16} height={16} alt="" />
                </button>
                <h1 className="qp-appbar-title">{t('qris.appbar')}</h1>
            </div>

            <div className="qp-scroll">
                <div className="qp-content">

                    {/* ── Timer & Status Section ── */}
                    <div className="qp-timer-section">
                        <h2 className="qp-title">{t('qris.title')}</h2>
                        <p className="qp-helper">
                            {t('qris.helper')}
                        </p>
                        {expiryTime && (
                            <div className={`qp-timer-badge${isExpired ? ' expired' : ''}`} aria-live="polite">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                    <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <span>{timerLabel}</span>
                            </div>
                        )}
                    </div>

                    {/* ── QR Card ── */}
                    <div className="qp-qr-card">
                        <img
                            src="https://d1qsbuaxtzmcn4.cloudfront.net/payment/qris-removebg-preview.png"
                            alt="QRIS"
                            className="qp-qris-banner"
                        />
                        <p className="qp-nmid">NMID: 00020101021226610014</p>
                        <div className="qp-qr-wrap" ref={qrRef}>
                            {qrString
                                ? <QRCode value={qrString} size={200} bgColor="#ffffff" fgColor="#000000" />
                                : <p className="qp-qr-empty">{t('qris.noQr')}</p>
                            }
                        </div>
                    </div>

                    {/* ── Action Buttons ── */}
                    {qrString && (
                        <div className="qp-actions">
                            <button
                                className="qp-btn-secondary"
                                onClick={handleCheckStatus}
                                disabled={checkingStatus || !orderId}
                                aria-label={t('qris.checkStatusAria')}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                    <path d="M12 2a10 10 0 1 0 10 10" strokeLinecap="round"/>
                                    <path d="M12 6v6l4 2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <span>{checkingStatus ? t('qris.checking') : t('qris.checkStatus')}</span>
                            </button>

                            <button
                                className="qp-btn-primary"
                                onClick={handleSaveOrDownload}
                                disabled={isExpired}
                                aria-label={supportsShare ? t('qris.saveToPhotos') : t('qris.saveQr')}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                    <path d="M12 3v12" strokeLinecap="round"/>
                                    <path d="M7 10l5 5 5-5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M5 21h14" strokeLinecap="round"/>
                                </svg>
                                <span>{t('qris.saveQr')}</span>
                            </button>
                        </div>
                    )}

                    {/* ── Status Message ── */}
                    {statusMessage && (
                        <p className="qp-status-msg" role="status" aria-live="polite">{t(statusMessage.key, statusMessage.vars)}</p>
                    )}

                    {/* ── Order Total — Frame 13 ── */}
                    {amount && (
                        <div className="qp-order-card">
                            <div className="qp-order-left">
                                <span className="qp-order-label">{t('qris.total')}</span>
                                <span className="qp-order-amount">{fmt(amount)}</span>
                            </div>
                            <button
                                className="qp-order-copy"
                                aria-label={t('qris.copyAmount')}
                                onClick={() => navigator.clipboard?.writeText(String(amount))}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                                </svg>
                            </button>
                        </div>
                    )}

                    {/* ── Cara Membayar — Frame 11 ── */}
                    <div className="qp-how-card">
                        <div className="qp-how-header">
                            <div className="qp-how-badge">!</div>
                            <h3 className="qp-how-title">{t('qris.howTitle')}</h3>
                        </div>
                        <div className="qp-how-list">
                            {t('qris.steps').map((step, i) => (
                                <div key={i} className="qp-how-item">
                                    <div className="qp-how-num">{String(i + 1).padStart(2, '0')}</div>
                                    <div className="qp-how-body">
                                        <span className="qp-how-step-title">{step.title}</span>
                                        <span className="qp-how-step-desc">{step.desc}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── Ada Kendala? — Frame 26 ── */}
                    <div className="qp-help-card">
                        <div className="qp-help-content">
                            <div className="qp-help-texts">
                                <p className="qp-help-title">{t('help.title')}</p>
                                <p className="qp-help-desc">{t('help.desc')}</p>
                            </div>
                            <a
                                className="qp-help-btn"
                                href={WA_CS_LINK}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t('help.cta')}
                            </a>
                        </div>
                        <img
                            className="qp-help-illustration"
                            src={`${process.env.PUBLIC_URL}/icons/ada-kendala-illustration.png`}
                            alt=""
                            aria-hidden="true"
                        />
                    </div>

                </div>
            </div>
        </div>
    );
};

export default QrisPage;
